import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiScissors, FiUser, FiHeart, FiStar } = FiIcons;

const Services = () => {
  const services = [
    {
      id: 1,
      title: 'Classic Haircut',
      price: 'AED 50',
      duration: '30 mins',
      description: 'Traditional men\'s haircut with scissors and clippers, styled to perfection.',
      image: 'https://i.ibb.co/qLGgY7dH/Nashmi-barber-shop-Cutting-1.jpg',
      icon: FiScissors,
      features: ['Consultation', 'Wash & Cut', 'Styling', 'Finish']
    },
    {
      id: 2,
      title: 'Premium Haircut & Beard',
      price: 'AED 80',
      duration: '45 mins',
      description: 'Complete grooming package including haircut and beard styling.',
      image: 'https://i.ibb.co/B24tDf5q/Nashmi-barber-shop-Cutting-1.jpg',
      icon: FiUser,
      features: ['Haircut', 'Beard Trim', 'Hot Towel', 'Styling']
    },
    {
      id: 3,
      title: 'Beard Styling',
      price: 'AED 30',
      duration: '20 mins',
      description: 'Professional beard trimming and shaping for the perfect look.',
      image: 'https://i.ibb.co/C3x4vDY8/Nashmi-barber-shop-Cutting-2.jpg',
      icon: FiScissors,
      features: ['Trim', 'Shape', 'Oil Treatment', 'Styling']
    },
    {
      id: 4,
      title: 'Face Treatment',
      price: 'AED 40',
      duration: '25 mins',
      description: 'Relaxing facial treatment with premium products.',
      image: 'https://i.ibb.co/p6G0qNWy/Nashmi-barber-shop-Cutting-3.jpg',
      icon: FiHeart,
      features: ['Cleansing', 'Face Mask', 'Moisturizing', 'Relaxation']
    },
    {
      id: 5,
      title: 'Hair Wash & Style',
      price: 'AED 25',
      duration: '15 mins',
      description: 'Professional hair washing and styling service.',
      image: 'https://i.ibb.co/cKht7Gmv/Nashmi-barber-shop-Cutting-4.jpg',
      icon: FiUser,
      features: ['Shampoo', 'Conditioning', 'Blow Dry', 'Styling']
    },
    {
      id: 6,
      title: 'Complete Grooming Package',
      price: 'AED 120',
      duration: '75 mins',
      description: 'Ultimate grooming experience with all services included.',
      image: 'https://i.ibb.co/d0LBbVMv/Nashmi-barber-shop-Hair-Style-1.jpg',
      icon: FiStar,
      features: ['Haircut', 'Beard Styling', 'Face Treatment', 'Hot Towel', 'Styling']
    }
  ];

  const deals = [
    {
      title: 'Student Special',
      discount: '20% OFF',
      description: 'Valid with student ID',
      image: 'https://i.ibb.co/Y7tk5fv9/Nashmi-barber-shop-deal-1.jpg'
    },
    {
      title: 'Weekend Package',
      discount: '15% OFF',
      description: 'Saturday & Sunday only',
      image: 'https://i.ibb.co/cXpTB2QY/Nashmi-barber-shop-deal-2.jpg'
    },
    {
      title: 'Monthly Membership',
      discount: '25% OFF',
      description: 'Unlimited visits per month',
      image: 'https://i.ibb.co/zHNj89c7/Nashmi-barber-shop-Deal-3.jpg'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="pt-20"
    >
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-luxury-charcoal to-luxury-darkCharcoal text-white">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl font-bold mb-6">
              Our <span className="text-luxury-gold">Premium</span> Services
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Experience the finest in men's grooming with our comprehensive range of professional services
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                className="bg-white dark:bg-luxury-charcoal rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4 bg-luxury-gold text-black px-3 py-1 rounded-full font-semibold">
                    {service.price}
                  </div>
                  <div className="absolute top-4 right-4 bg-black/50 p-2 rounded-full">
                    <SafeIcon icon={service.icon} className="w-5 h-5 text-luxury-gold" />
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">{service.description}</p>
                  <p className="text-sm text-luxury-gold font-semibold mb-4">Duration: {service.duration}</p>
                  
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm">Includes:</h4>
                    <ul className="space-y-1">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="text-sm text-gray-600 dark:text-gray-300 flex items-center">
                          <span className="w-2 h-2 bg-luxury-gold rounded-full mr-2"></span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <button className="w-full mt-6 bg-luxury-gold text-black py-2 rounded-lg font-semibold hover:bg-luxury-darkGold transition-colors duration-300">
                    Book Now
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Special Deals - Fixed Image Display */}
      <section className="py-16 bg-gray-50 dark:bg-luxury-charcoal">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-4">
              <span className="text-luxury-gold">Special</span> Deals
            </h2>
            <p className="text-gray-600 dark:text-gray-300">Save more with our exclusive offers</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {deals.map((deal, index) => (
              <motion.div
                key={index}
                className="bg-white dark:bg-luxury-darkCharcoal rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="relative overflow-hidden h-56">
                  <img
                    src={deal.image}
                    alt={deal.title}
                    className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute top-4 right-4 bg-luxury-gold text-black px-3 py-1 rounded-full font-bold text-lg">
                    {deal.discount}
                  </div>
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-xl font-bold mb-3 text-luxury-gold">{deal.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">{deal.description}</p>
                  <button className="bg-luxury-gold text-black px-6 py-2 rounded-full font-semibold hover:bg-luxury-darkGold transition-colors duration-300">
                    Claim Offer
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="py-16 bg-luxury-gold">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-black mb-4">Ready for a Premium Experience?</h2>
            <p className="text-xl text-black/80 mb-8">Book your appointment today and experience luxury grooming</p>
            <a
              href="tel:+971507096230"
              className="bg-black text-luxury-gold px-8 py-4 rounded-full font-semibold hover:bg-gray-800 transition-colors duration-300 inline-block"
            >
              Call Now: +971 50 709 6230
            </a>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default Services;